import React from "react";
import BaseModal from "@reusables/Modals/BaseModal";
import { Trans, useTranslation } from "react-i18next";
import BaseButton from "@reusables/BaseButton";
import { ReactComponent as CustomerIcon } from "@assets/icons/sidebar/ic_person.svg";
import { ReactComponent as SuppliersIcon } from "@assets/icons/sidebar/ic_package_delivery.svg";
import { ReactComponent as AllProductsIcon } from "@assets/icons/sidebar/ic_small_bag.svg";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { Alert, Collapse } from "@mui/material";
import { useImportPOGODataMutation } from "@redux/features/integrations/powerofficeApi";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { useGetModuleLocationsQuery } from "@redux/features/locations/locationsApi";
import { isDataTransferStateActive } from "@redux/features/dfs/types";
import { selectOperation } from "@redux/features/dfs/dfsSlice";
import { performImport } from "@redux/features/dfs/thunks";
import { Integration } from "@/types/general";
import { getIntegrationImportHistory, isReimportConfirmationError } from "../shared/importHistory";
import { useReimportGuard } from "../shared/useReimportGuard";
import ReimportConfirmation from "../shared/ReimportConfirmation";
import ImportEntitySummaryCard from "../shared/ImportEntitySummaryCard";

interface PowerofficeImportModalProps {
    integration: Integration.Components.Integration;
    isOpen: boolean;
    onClose: () => void;
}

const formSchema = z.object({
    location: z.object({
        store: z.object({
            id: z.number(),
            name: z.string(),
        }),
        section: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),
    }),
});

type FormTyping = z.infer<typeof formSchema>;

const importPlan: Integration.Import.Plan = {
    integration: "poweroffice",
    entities: ["customer", "supplier", "product"],
};

export default function PowerofficeImportModal({
    integration,
    isOpen,
    onClose,
}: PowerofficeImportModalProps) {
    const { t } = useTranslation("", {
        keyPrefix: "imports.modules.poweroffice.importModal",
    });

    const dispatch = useAppDispatch();

    const [importPOGO, { isLoading: isImportLoading }] = useImportPOGODataMutation();

    const isImportInProgress = useAppSelector((state) =>
        isDataTransferStateActive(selectOperation("import")(state))
    );

    const { data: locationsOptions, isLoading: isLocationsOptionsLoading } =
        useGetModuleLocationsQuery("integrations");

    const form = useForm<FormTyping>({
        resolver: zodResolver(formSchema),
    });

    const historyByEntity = getIntegrationImportHistory(integration);
    const reimportGuard = useReimportGuard({ historyByEntity });

    const handleClose = () => {
        reimportGuard.cancelConfirmation();
        onClose();
    };

    const onSubmit = form.handleSubmit((data) => {
        void reimportGuard.requestImport(
            importPlan,
            ({ reimportConfirmed }) =>
                dispatch(
                    performImport(
                        "poweroffice",
                        () => {
                            return importPOGO({
                                store_id: data.location.store.id,
                                section_id: data.location.section?.id,
                                ...(reimportConfirmed && { reimport_confirmed: true }),
                            }).unwrap();
                        },
                        { shouldSuppressErrorToast: isReimportConfirmationError }
                    )
                ),
            () => {
                form.reset();
                handleClose();
            }
        );
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            width={800}
            padding="45px"
            useCloseIcon
            isLoading={isImportLoading && !reimportGuard.pendingImport}
        >
            {reimportGuard.pendingImport ? (
                <ReimportConfirmation
                    integrationName={integration.name}
                    riskyEntities={reimportGuard.pendingImport.riskyEntities}
                    safeEntities={reimportGuard.pendingImport.safeEntities}
                    onBack={reimportGuard.cancelConfirmation}
                    onConfirm={() => void reimportGuard.confirmImport()}
                    isLoading={reimportGuard.isConfirmingImport}
                />
            ) : (
                <form onSubmit={onSubmit}>
                    <div className="text-xl font-semibold text-center text-accent mb-4">
                        {t("heading")}
                    </div>

                    <Collapse in={isImportInProgress}>
                        <Alert severity={"warning"} className={"mb-4"}>
                            {t("importIsAlreadyInProgressAlert")}
                        </Alert>
                    </Collapse>

                    {/* What we import */}
                    <div className={"space-y-2 mb-4"}>
                        <div className="font-semibold text-inputs-label-dim">
                            {t("whatWeImport.label")}
                        </div>
                        <div className={"grid grid-cols-3 gap-2 text-accent"}>
                            <ImportEntitySummaryCard
                                label={t("whatWeImport.products")}
                                icon={<AllProductsIcon width={24} height={24} />}
                                history={historyByEntity.product}
                            />
                            <ImportEntitySummaryCard
                                label={t("whatWeImport.customers")}
                                icon={<CustomerIcon width={24} height={24} />}
                                history={historyByEntity.customer}
                            />
                            <ImportEntitySummaryCard
                                label={t("whatWeImport.suppliers")}
                                icon={<SuppliersIcon width={24} height={24} />}
                                history={historyByEntity.supplier}
                            />
                        </div>
                    </div>
                    <div className={"space-y-2 mb-4"}>
                        <div className="font-semibold text-inputs-label-dim">
                            {t("rules.label")}:
                        </div>
                        <ul className={"list-disc ml-4 text-accent"}>
                            <li>{t("rules.sync")};</li>
                            <li>
                                <Trans
                                    t={t}
                                    i18nKey="rules.masterStock"
                                    components={{ b: <strong /> }}
                                />
                            </li>
                        </ul>
                    </div>

                    <div>
                        <Controller
                            name={"location"}
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    error={fieldState.error}
                                    label={t("locationDropdown.label") + " *"}
                                    placeholder={t("locationDropdown.placeholder")}
                                    options={locationsOptions}
                                    getter={{
                                        label: (opt) =>
                                            `${opt.store.name}${
                                                opt.section ? ` - ${opt.section.name}` : ""
                                            }`,
                                        key: (opt) =>
                                            opt.section
                                                ? `${opt.store.id}_${opt.section.id}`
                                                : `${opt.store.id}`,
                                        renderOption: (opt, icon) => (
                                            <div>
                                                <div className="grow">
                                                    <span className="bold-highlight">
                                                        {opt.store.name}
                                                    </span>
                                                    {opt.section ? (
                                                        <> - {opt.section.name}</>
                                                    ) : null}
                                                </div>
                                                {icon}
                                            </div>
                                        ),
                                    }}
                                    isLoading={isLocationsOptionsLoading}
                                    autocomplete
                                    virtualize
                                />
                            )}
                        />
                    </div>

                    <div className={"mt-8 grid grid-cols-2 gap-2"}>
                        <BaseButton
                            buttonWidth="100%"
                            text={t("buttons.cancel")}
                            size="md"
                            type="button"
                            primaryOutlined
                            onClick={handleClose}
                        />
                        <BaseButton
                            buttonWidth="100%"
                            text={t("buttons.import")}
                            size="md"
                            type="submit"
                            loading={isImportInProgress}
                        />
                    </div>
                </form>
            )}
        </BaseModal>
    );
}
